import React from "react";
import { useNavigate } from "react-router";
import { Center, Row } from "@/shared/ui/Blocks";
import { Icon } from "@/shared/ui/Icon";
import { SubtitleText } from "@/shared/ui/Typography";
import { Button } from "@/shared/ui/Button";
import { cn } from "@/shared/utils/shadcn";

interface ProgressIndicatorProps {
  completedCount?: number;
  totalCount: number;
  color?: "blue" | "green" | "grey" | "orange";
}

interface ProgressIndicatorCTAProps extends ProgressIndicatorProps {
  label: string;
  buttonLabel: string;
  link?: string;
  className?: string;
  visible?: boolean;
}

const colorMap = {
  blue: "bg-blue-500",
  green: "bg-green-500",
  grey: "bg-grey-300",
  orange: "bg-orange-500",
};

export function ProgressIndicator(props: ProgressIndicatorProps) {
  const { completedCount = 0, totalCount, color } = props;
  const completePercent = (completedCount / totalCount) * 100;
  return (
    <div className="h-1 w-[40px] rounded-md bg-grey-300">
      <div
        style={{
          width: `${completePercent}%`,
        }}
        className={cn("bg-primary h-full rounded-md", color && colorMap[color])}
      />
    </div>
  );
}

function ProgressIndicatorCTA(props: ProgressIndicatorCTAProps) {
  const {
    label,
    completedCount = 0,
    totalCount,
    buttonLabel,
    link,
    visible = true,
    className,
    color = "orange",
  } = props;
  const completePercent = (completedCount / totalCount) * 100;
  const navigate = useNavigate();

  const handleButtonClick = () => {
    navigate(link || "/");
  };

  if (!visible) return null;

  return (
    <Row className={`items-center justify-between flex-wrap ${className}`}>
      <Row className="items-center space-x-4">
        <Center className="w-[18px] h-[18px] border border-solid rounded-full border-orange-500">
          <Icon icon="CheckLarge" color="orange" size="xs" />
        </Center>
        <h5>{label}</h5>
        <Row className="items-center space-x-2">
          <SubtitleText>{`${completedCount} of ${totalCount}`}</SubtitleText>
          <ProgressIndicator
            completedCount={completedCount}
            totalCount={totalCount}
          />
        </Row>
      </Row>
      <Button
        variant="link"
        className="text-primary self-end p-0"
        onClick={() => handleButtonClick()}
        size="sm"
      >
        {buttonLabel}
      </Button>
    </Row>
  );
}

export { ProgressIndicatorCTA };
